import { AreaChart, BarChart } from "@mantine/charts";
import { useReportingStore } from "../../../../../stores/ReportingStore";
import { ColorSwatch, Group, Paper, Text } from "@mantine/core";
import LoaderStats from "../../LoaderStats";
import LinkedinPerf from "../perf/LinkedinPerf";
import { useMemo } from "react";
import { getChartColors } from "../../../../../functions/utilFunctions";
import { useLanguage } from "../../../../../stores/LanguageStore";

function transformData(data, type) {
  const result = [];

  data.forEach((entry) => {
    const campaignId = entry.prog.id;

    entry.stats.forEach((stat) => {
      // Check if the date already exists in the result
      let existingEntry = result.find((r) => r.date === stat.date);

      if (!existingEntry) {
        // If not, create a new entry for this date
        existingEntry = { date: stat.date };
        result.push(existingEntry);
      }

      // Add the field for the current campaign
      existingEntry[`sent_${campaignId}`] = stat[type.field];
    });
  });

  return result;
}

const LinkedinSummary = ({ type }) => {
  const lang = useLanguage((s) => s.language);
  const statisticsDate = useReportingStore((s) => s.actualStatsDate);
  const dateRange = useReportingStore((s) => s.dateRange);
  const programmations = useReportingStore((s) => s.programmations);
  const progStats = useReportingStore((s) => s.progStats);

  const data = useMemo(() => {
    return transformData(progStats, type);
  }, [progStats, type]);

  const series = useMemo(() => {
    return progStats.map((item, index) => ({
      name: `sent_${item.prog.id}`,
      label: `${item.prog.workflow_name}`,
      color: `${getChartColors()[index]}.5`,
    }));
  }, [progStats]);

  return (
    <Group gap={"xl"} wrap="nowrap" flex={1} align="stretch">
      <Paper pos={"relative"} flex={1} p={"xl"} pt={"md"}>
        <LoaderStats
          isDate={programmations.length < 2}
          isProgStats={programmations.length > 1}
        />

        <Group mb={32} justify="center">
          <Text size="lg" fw={600}>
            {type.label}{" "}
            {!dateRange.end_date && !dateRange.start_date
              ? lang.reports.last_60_days
              : lang.reports.per_date}
          </Text>
        </Group>

        {programmations.length > 1 ? (
          <>
            <AreaChart
              h={300}
              w={"100%"}
              data={data}
              type="default"
              dataKey="date"
              withDots
              withGradient
              series={series}
              // tickLine="y"
              barProps={{ isAnimationActive: true, animationDuration: 600 }}
            />
            <Group mt={16} justify="center" gap={"xl"}>
              {progStats.map((item, index) => {
                return (
                  <Group key={item.prog.id} gap={"xs"}>
                    <ColorSwatch
                      color={`var(--mantine-color-${
                        getChartColors()[index]
                      }-5)`}
                      size={16}
                    />
                    <Text size="sm">
                      {item.prog.workflow_name} #{item.prog.id}
                    </Text>
                  </Group>
                );
              })}
            </Group>
          </>
        ) : (
          <BarChart
            h={300}
            w={"100%"}
            data={statisticsDate}
            dataKey="date"
            series={[{ name: type.field, label: type.label, color: "blue.6" }]}
            tickLine="y"
            yAxisLabel="Volume"
            xAxisLabel="Date"
            barProps={{
              isAnimationActive: true,
              animationDuration: 600,
              radius: [8, 8, 0, 0],
            }}
          />
        )}
      </Paper>

      {type.type !== "visits" && <LinkedinPerf type={type} />}
    </Group>
  );
};

export default LinkedinSummary;
